import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Gift, Rocket, Check, Coins } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // step 1 = choose plan, step 2 = form
  const [plan, setPlan] = useState(loc.state?.plan || null); // 'member' | 'developer'
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (plan === "developer" && !confirm) {
      setErr("Please confirm the $20/year auto-renewing subscription");
      return;
    }
    setBusy(true);
    try {
      await register(email, password, name, plan === "developer");
      // Auto-redeem a Jixxeo Power referral code if it was passed via ?ref= or ?next=/register?ref=
      try {
        const params = new URLSearchParams(loc.search);
        const ref = (params.get("ref") || "").trim().toUpperCase();
        if (ref) {
          const { powerRedeemReferral } = await import("@/lib/powerApi");
          await powerRedeemReferral(ref).catch(() => {});
        }
      } catch { /* non-blocking */ }
      const nextParam = new URLSearchParams(loc.search).get("next");
      const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
      toast.success(
        plan === "developer" ? "Welcome, creator! Subscription active." : "Welcome to Jixxeo — 50 coins added!",
        { description: plan === "developer" ? "Publish your first app any time." : "Enter the feed and swipe." }
      );
      if (safeNext && safeNext.startsWith("/api/sso/")) { window.location.href = safeNext; return; }
      nav(safeNext || (plan === "developer" ? "/dev/publish" : "/feed"));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Signup failed");
    } finally { setBusy(false); }
  };

  if (!plan) return <PlanChooser onPick={setPlan} />;

  const isDev = plan === "developer";
  const accent = isDev ? "#00F0FF" : "#39FF14";

  return (
    <div data-testid={AUTH.registerRoot} className="min-h-[85vh] flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <button onClick={() => setPlan(null)} className="mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white mb-6">
          ← Change plan
        </button>
        <div className="mb-6 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl items-center justify-center mb-4"
            style={{ background: accent, boxShadow: `0 0 24px ${accent}55` }}>
            {isDev ? <Rocket className="w-7 h-7 text-black" strokeWidth={3} /> : <Zap className="w-7 h-7 text-black" strokeWidth={3} />}
          </div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: accent }}>
            // {isDev ? "creator account" : "member account"}
          </div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tighter">
            {isDev ? "Become a Creator" : "Join the Feed"}
          </h1>
          <div className="mt-3 inline-flex items-center gap-2 mono text-xs px-3 py-1 rounded-full border"
            style={{ background: `${isDev ? "#00F0FF" : "#FFE300"}10`,
                     color: isDev ? "#00F0FF" : "#FFE300",
                     borderColor: `${isDev ? "#00F0FF" : "#FFE300"}30` }}>
            {isDev ? <><Rocket className="w-3 h-3" /> $20/year · auto-renews</> : <><Gift className="w-3 h-3" /> Free forever · 50 welcome coins</>}
          </div>
        </div>
        <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
          <Field label="Display Name" testId={AUTH.nameInput} type="text" value={name} onChange={setName} placeholder="Your creator handle" required={false} accent={accent} />
          <Field label="Email" testId={AUTH.emailInput} type="email" value={email} onChange={setEmail} placeholder="you@jixxeo.io" accent={accent} />
          <Field label="Password" testId={AUTH.passwordInput} type="password" value={password} onChange={setPassword} placeholder="At least 6 characters" minLength={6} accent={accent} />

          {isDev && (
            <label className="flex items-start gap-3 p-3 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/5">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-1 accent-[#00F0FF]" />
              <span className="text-xs text-white/80">
                I agree to the <span className="text-[#00F0FF]">$20/year Creator subscription</span>, billed today
                and auto-renewing yearly. Cancel any time from your profile — access continues to end of term.
              </span>
            </label>
          )}

          {err && <div data-testid={AUTH.error} className="mono text-xs text-[#FF003C] bg-[#FF003C]/10 border border-[#FF003C]/30 rounded-lg p-3">{err}</div>}

          <button data-testid={AUTH.submit} disabled={busy}
            className="w-full font-bold mono text-xs uppercase tracking-widest py-3 rounded-full transition-colors disabled:opacity-50 text-black"
            style={{ background: accent }}>
            {busy ? "Creating…" : isDev ? "Create Creator Account · $20" : "Create Free Account"}
          </button>
          <div className="text-center text-sm text-white/50">
            Have an account?{" "}
            <Link data-testid={AUTH.toggle} to="/login" className="text-[#00F0FF] hover:text-white">Sign in →</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlanChooser({ onPick }) {
  const plans = [
    {
      id: "member", accent: "#39FF14", eyebrow: "// member", title: "Free Member", price: "Free",
      bullets: ["Browse & swipe the feed", "50 welcome coins", "Tip creators", "Save favorites", "Comment & follow"],
      icon: <Gift className="w-6 h-6" />, cta: "Continue Free",
    },
    {
      id: "developer", accent: "#00F0FF", eyebrow: "// creator", title: "Creator Plan", price: "$20 / year",
      bullets: ["Everything in Free", "Publish apps (URL, ZIP, GitHub)", "Manage releases & versions", "Get tipped by users", "Analytics dashboard", "Auto-renews yearly"],
      icon: <Rocket className="w-6 h-6" />, cta: "Continue as Creator",
    },
  ];
  return (
    <div data-testid={AUTH.planChoice} className="min-h-[85vh] flex items-center justify-center p-6">
      <div className="w-full max-w-4xl">
        <div className="mb-10 text-center">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// step 1 of 2</div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl uppercase tracking-tighter">Choose your side</h1>
          <p className="text-white/60 mt-2">Members swipe. Creators ship. You can upgrade any time.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {plans.map((p, i) => (
            <motion.button
              key={p.id}
              data-testid={p.id === "member" ? AUTH.memberPath : AUTH.developerPath}
              onClick={() => onPick(p.id)}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className="text-left glass rounded-3xl p-8 relative overflow-hidden group transition-[transform,border-color] hover:-translate-y-1"
              style={{ borderColor: `${p.accent}30` }}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                   style={{ background: `radial-gradient(circle at 30% 20%, ${p.accent}18, transparent 60%)` }} />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-black"
                       style={{ background: p.accent }}>{p.icon}</div>
                  <div className="mono text-[10px] uppercase tracking-widest" style={{ color: p.accent }}>{p.eyebrow}</div>
                </div>
                <h3 className="font-heading font-black text-3xl uppercase tracking-tighter">{p.title}</h3>
                <div className="font-heading font-black text-4xl mt-2" style={{ color: p.accent }}>{p.price}</div>
                <ul className="space-y-2 mt-6">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-white/80">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.accent }} /> {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 inline-flex items-center gap-2 mono text-xs uppercase tracking-widest font-bold py-2 px-4 rounded-full text-black"
                     style={{ background: p.accent }}>
                  {p.cta} →
                </div>
              </div>
            </motion.button>
          ))}
        </div>
        <div className="text-center mt-8 text-sm text-white/50">
          Already have an account? <Link to="/login" className="text-[#00F0FF]">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, type, value, onChange, placeholder, required = true, minLength, accent = "#00F0FF" }) {
  return (
    <label className="block">
      <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{label}</div>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} minLength={minLength}
        className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 focus:outline-none text-sm"
        style={{ borderColor: undefined }}
        onFocus={(e) => (e.target.style.borderColor = `${accent}80`)}
        onBlur={(e) => (e.target.style.borderColor = "")} />
    </label>
  );
}
